import axios from "axios";
import { baseURL } from "../api";

const ACCESS_TOKEN_KEY = "bits.accessToken";
let refreshPromise = null;
let refreshTimer = null;

export const getAccessToken = () => sessionStorage.getItem(ACCESS_TOKEN_KEY);

export const setAccessToken = token => {
  if (token) {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  } else {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  }
  localStorage.removeItem("token");
  scheduleRefresh(token);
  window.dispatchEvent(new CustomEvent("bits-auth-token", { detail: token || null }));
};

export const clearAuthentication = reason => {
  if (refreshTimer) window.clearTimeout(refreshTimer);
  refreshTimer = null;
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem("twoFactorData");
  sessionStorage.removeItem("loginFormData");
  sessionStorage.removeItem("twoFactorAuthScreenIsOpen");
  localStorage.removeItem("token");
  localStorage.removeItem("userData");
  window.dispatchEvent(new CustomEvent("bits-auth-logout", { detail: reason || "SESSION_REVOKED" }));
};

const scheduleRefresh = token => {
  if (refreshTimer) window.clearTimeout(refreshTimer);
  refreshTimer = null;
  if (!token) return;
  try {
    let encodedPayload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    encodedPayload += "=".repeat((4 - encodedPayload.length % 4) % 4);
    const payload = JSON.parse(atob(encodedPayload));
    const delay = Math.max(1000, payload.exp * 1000 - Date.now() - 60000);
    refreshTimer = window.setTimeout(() => {
      refreshSession().catch(() => clearAuthentication("SESSION_REVOKED"));
    }, delay);
  } catch (_) {
    clearAuthentication("INVALID_TOKEN");
  }
};

export const refreshSession = async () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${baseURL}/user/refresh-token`, {}, { withCredentials: true, skipAuthRefresh: true })
      .then(response => {
        const token = response?.data?.access_token;
        if (!token) throw new Error("Refresh response did not contain an access token");
        setAccessToken(token);
        return token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

axios.defaults.withCredentials = true;

axios.interceptors.request.use(config => {
  const token = getAccessToken();
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  response => response,
  async error => {
    const config = error?.config || {};
    const status = error?.response?.status;
    const code = error?.response?.data?.metadata?.statusCode;
    const isAuthEndpoint = String(config.url || "").includes("/user/login")
      || String(config.url || "").includes("/user/2FA/")
      || String(config.url || "").includes("/user/refresh-token")
      || String(config.url || "").includes("/user/forgot/password");

    if (status === 401 && !config.skipAuthRefresh && !config.__bitsRetried && !isAuthEndpoint
        && !["SESSION_REPLACED", "SESSION_REVOKED", "USER_DISABLED"].includes(code)) {
      config.__bitsRetried = true;
      try {
        const token = await refreshSession();
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
        return axios(config);
      } catch (_) {
        clearAuthentication(code || "SESSION_REVOKED");
      }
    } else if (status === 401 && ["SESSION_REPLACED", "SESSION_REVOKED", "USER_DISABLED"].includes(code)) {
      clearAuthentication(code);
    }
    return Promise.reject(error);
  }
);

scheduleRefresh(getAccessToken());
